import { BulkActionBar } from '@/components/bulkQueue/BulkActionBar';
import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import {
    ClinicianSelectionCell,
    ClinicianSelectionHeader,
} from '@/components/bulkQueue/cells/clinician/ClinicianSelection';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import { BulkClinicianAssignmentTableQuery } from '@/components/bulkQueue/tables/clinician/BulkClinicianAssignmentTable.gql.types';
import {
    BulkClinicianContextProvider,
    useBulkClinicianContext,
} from '@/components/bulkQueue/tables/clinician/BulkClinicianContext';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { BulkTableRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { VisitColumns } from '@/components/visits/tables/columns';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { useDialog } from '@/dialogs/DialogContext';
import { DialogType } from '@/dialogs/DialogManager';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { Button, Checkbox, CheckboxProps, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

export const BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkClinicianAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
    ) {
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering) {
            totalCount
            items {
                ...VisitTableRow
            }
        }
    }
    ${VisitTableRow}
`;

type BulkClinicianAssignmentTableProps = {
    title?: string;
    rows: BulkTableRow[];
    pagination: Pagination;
    serverSideSort: ServerSideSort;
    hasBulkReassign: boolean;
    selectedRowValues: string[];
    setSelectedRowValues: (values: string[]) => void;
};

const getColumns = (isLocked: boolean): RawColumn<BulkTableRow>[] => [
    {
        field: 'sortPriority',
        label: 'Priority',
        rowToComponent: (entity) => <PriorityRenderCell entity={entity} />,
        options: { ...VisitColumns.priorityColumnOptions, sort: false },
    },
    {
        field: 'patient.fullName',
        label: 'Patient',
        rowToComponent: (entity) => <PatientRenderCell entity={entity} />,
    },
    {
        field: 'sortServicesName',
        label: 'Services',
        rowToComponent: (entity) => <ServicesRenderCell entity={entity} />,
        options: { sort: false },
    },
    {
        field: 'cliniciansSortKey',
        label: 'Clinician',
        rowToComponent: (entity) => <ClinicianSelectionCell entity={entity} isLocked={isLocked} />,
        options: {
            sort: false,
            setCellProps: () => getSetCellProps(true),
            customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                <ClinicianSelectionHeader
                    key={columnMeta.name}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                    {...{ isLocked }}
                />
            ),
        },
    },
    {
        field: 'sortDate',
        label: 'Date',
        rowToComponent: (entity) => <DateRenderCell entity={entity} />,
    },
    {
        field: 'fulfillmentWindow.startDate',
        label: 'Fulfillment Window',
        rowToComponent: (entity) => <FulfillmentWindowRenderCell entity={entity} />,
    },
    {
        field: 'orderSortKey',
        label: 'Order',
        rowToComponent: (entity) => <OrderRenderCell entity={entity} />,
        options: { sort: false },
    },
    {
        field: '',
        label: 'Actions',
        rowToComponent: (entity) => <ActionsRenderCell entity={entity} />,
        options: { sort: false },
    },
];

const rowToSelectionId = (row: BulkTableRow) => row.id;

export const BulkClinicianAssignmentTable: FC<BulkClinicianAssignmentTableProps> = (props) => {
    const {
        rows,
        pagination,
        title,
        serverSideSort,
        hasBulkReassign,
        selectedRowValues,
        setSelectedRowValues,
    } = props;

    const { selectedCount: clinicianSelectedCount } = useBulkClinicianContext();
    const hasClinicianSelections = clinicianSelectedCount > 0;
    const isLocked = selectedRowValues.length > 0;
    const columns = useMemo(() => getColumns(isLocked), [isLocked]);

    const disabledCheckboxComponents = useMemo(
        () => ({
            Checkbox: (props: CheckboxProps) => <Checkbox {...props} disabled />,
        }),
        [],
    );

    return (
        <AxleTable
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            dynamicPaging={pagination}
            data={rows}
            rowToLink={rowToLink}
            columns={columns}
            toolbar={false}
            serverSideSort={serverSideSort}
            selectableRows={hasBulkReassign ? 'multiple' : 'none'}
            rowToSelectionIdentifier={hasBulkReassign ? rowToSelectionId : undefined}
            selectedRowValues={hasBulkReassign ? selectedRowValues : undefined}
            setSelectedRowValues={hasBulkReassign ? setSelectedRowValues : undefined}
            components={hasClinicianSelections ? disabledCheckboxComponents : undefined}
        />
    );
};

type ReassignVisitsActionBarProps = {
    rows: BulkTableRow[];
    selectedRowValues: string[];
};

const ReassignVisitsActionBar: FC<ReassignVisitsActionBarProps> = ({ rows, selectedRowValues }) => {
    const { openDialog } = useDialog();
    const selectedCount = selectedRowValues.length;

    const handleReassign = useCallback(() => {
        const selectedSet = new Set(selectedRowValues);
        const visitsToReassign: string[] = [];
        const visitRequestsToReassign: string[] = [];

        rows.forEach((row) => {
            if (!selectedSet.has(row.id)) return;
            if (row.__typename === 'VisitRequest') {
                visitRequestsToReassign.push(row.id);
            } else {
                visitsToReassign.push(row.id);
            }
        });

        openDialog(DialogType.ReassignVisits, {
            visitsToReassign,
            visitRequestsToReassign,
            cliniciansToReassign: [],
        });
    }, [openDialog, rows, selectedRowValues]);

    return (
        <BulkActionBar visible={selectedCount > 0}>
            <Button variant='contained' size='small' onClick={handleReassign}>
                Reassign {selectedCount} Visit{selectedCount !== 1 ? 's' : ''}
            </Button>
        </BulkActionBar>
    );
};

type ConnectedBulkClinicianAssignmentTableProps = {
    title?: string;
};

export const ConnectedBulkClinicianAssignmentTable: FC<
    ConnectedBulkClinicianAssignmentTableProps
> = (props) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } =
        useConnectedBulkTable<BulkClinicianAssignmentTableQuery>({
            query: BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY,
        });
    const hasBulkReassign = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_BULK_VISIT_REASSIGNMENT);
    const [selectedRowValues, setSelectedRowValues] = useState<string[]>([]);

    // Prune selections to only include IDs still in the displayed rows.
    // When filters change and rows update, stale selections are removed.
    useEffect(() => {
        const rowIds = new Set(rows.map((r) => r.id));
        setSelectedRowValues((prev) => {
            const filtered = prev.filter((id) => rowIds.has(id));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [rows]);

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkClinicianContextProvider entities={rows}>
            {hasBulkReassign && (
                <ReassignVisitsActionBar rows={rows} selectedRowValues={selectedRowValues} />
            )}
            <BulkClinicianAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
                hasBulkReassign={hasBulkReassign}
                selectedRowValues={selectedRowValues}
                setSelectedRowValues={setSelectedRowValues}
            />
        </BulkClinicianContextProvider>
    );
};
